import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Language } from '@/services/interfaces/Translation';

interface LanguageContextType {
  sourceLanguage: Language;
  targetLanguage: Language;
  setSourceLanguage: (language: Language) => void;
  setTargetLanguage: (language: Language) => void;
  swapLanguages: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // 默认语言配置
  const [sourceLanguage, setSourceLanguage] = useState<Language>(Language.MandarinChinese);
  const [targetLanguage, setTargetLanguage] = useState<Language>(Language.English);

  // 使用 useCallback 稳定函数引用
  const swapLanguages = useCallback(() => {
    const temp = sourceLanguage;
    setSourceLanguage(targetLanguage);
    setTargetLanguage(temp);
  }, [sourceLanguage, targetLanguage]);

  // 使用 useMemo 稳定 context value，避免不必要的重渲染
  const value = useMemo((): LanguageContextType => ({
    sourceLanguage,
    targetLanguage,
    setSourceLanguage,
    setTargetLanguage,
    swapLanguages
  }), [sourceLanguage, targetLanguage, swapLanguages]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 